
import { useRef, useState, useEffect } from 'react';

interface Testimonial {
  id: number;
  name: string;
  role: string;
  company: string;
  quote: string;
  rating: number;
  avatar: string;
}

export default function Testimonials() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  // ---------- Intersection Observer ----------
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // ---------- Testimonial data ----------
  const testimonials: Testimonial[] = [
    {
      id: 1,
      name: 'Sarah Mitchell',
      role: 'Marketing Director',
      company: 'GlowLab Beauty',
      quote:
        'Working with James was an absolute game-changer for our brand. The content was authentic, engaging, and drove a 300% increase in our conversion rates. His understanding of our target audience was spot-on.',
      rating: 5,
      avatar:
        'https://readdy.ai/api/search-image?query=professional%20business%20woman%20portrait%20headshot%20confident%20smile%20corporate%20attire%20neutral%20background&width=100&height=100&seq=testimonial-1&orientation=squarish'
    },
    {
      id: 2,
      name: 'Michael Chen',
      role: 'Brand Manager',
      company: 'TechPulse',
      quote:
        'The quality of content exceeded our expectations. Every video was professionally crafted with attention to detail. Our engagement metrics skyrocketed, and we saw immediate ROI from the campaign.',
      rating: 5,
      avatar:
        'https://readdy.ai/api/search-image?query=asian%20business%20man%20professional%20headshot%20confident%20expression%20modern%20office%20background&width=100&height=100&seq=testimonial-2&orientation=squarish'
    },
    {
      id: 3,
      name: 'Emily Rodriguez',
      role: 'E-commerce Lead',
      company: 'ActiveLife Fitness',
      quote:
        "James' ability to connect with our audience was remarkable. The UGC content felt genuine and relatable, which is exactly what we needed. Sales increased by 250% during the campaign period.",
      rating: 5,
      avatar:
        'https://readdy.ai/api/search-image?query=latina%20business%20woman%20professional%20portrait%20friendly%20smile%20corporate%20setting&width=100&height=100&seq=testimonial-3&orientation=squarish'
    },
    {
      id: 4,
      name: 'David Thompson',
      role: 'CEO',
      company: 'BrewMaster Coffee',
      quote:
        "Professional, creative, and results-driven. James perfectly captured our brand essence and resonated with coffee enthusiasts. We've continued working together for multiple campaigns.",
      rating: 5,
      avatar:
        'https://readdy.ai/api/search-image?query=mature%20business%20man%20ceo%20portrait%20professional%20attire%20confident%20leadership%20neutral%20background&width=100&height=100&seq=testimonial-4&orientation=squarish'
    },
    {
      id: 5,
      name: 'Jessica Park',
      role: 'Social Media Manager',
      company: 'UrbanStep Footwear',
      quote:
        'The turnaround time was impressive, and the content quality was consistently high. Our social media engagement doubled, and we gained thousands of new followers. Highly recommend James!',
      rating: 5,
      avatar:
        'https://readdy.ai/api/search-image?query=young%20professional%20woman%20headshot%20creative%20industry%20modern%20style%20bright%20background&width=100&height=100&seq=testimonial-5&orientation=squarish'
    }
  ];

  // ---------- Scroll helpers ----------
  const checkScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    setCanScrollLeft(scrollLeft > 0);
    // Subtract a small tolerance to avoid flickering when at the far right
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 5);
  };

  const scroll = (direction: 'left' | 'right') => {
    const el = scrollRef.current;
    if (!el) return;
    const scrollAmount = 380;
    el.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth'
    });
  };

  // ---------- Attach scroll listener ----------
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const onScroll = () => {
      try {
        checkScroll();
      } catch (error) {
        console.error('Error while checking scroll position:', error);
      }
    };

    el.addEventListener('scroll', onScroll);
    // Initial check
    checkScroll();

    return () => {
      el.removeEventListener('scroll', onScroll);
    };
  }, []);

  // ---------- Render ----------
  return (
    <section
      id="testimonials"
      ref={sectionRef}
      className="py-16 sm:py-24 md:py-32 bg-[#1a1a1a] relative overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
        {/* Section Header */}
        <div
          className={`mb-10 sm:mb-16 transition-all duration-1000 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="w-12 sm:w-16 h-1 bg-[#00D9FF] mb-4 sm:mb-6"></div>
          <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold text-white tracking-tight">
            CLIENT LOVE
          </h2>
        </div>

        {/* Testimonials Scroll Container */}
        <div
          ref={scrollRef}
          className="flex gap-4 sm:gap-6 md:gap-8 overflow-x-auto pb-6 sm:pb-8 cursor-grab active:cursor-grabbing"
          style={{ scrollbarWidth: 'none' }}
        >
          {testimonials.map((testimonial, index) => (
            <div
              key={testimonial.id}
              className={`flex-shrink-0 w-[300px] sm:w-[380px] md:w-[450px] bg-[#2a2a2a] rounded-2xl sm:rounded-[28px] p-6 sm:p-8 md:p-12 transition-all duration-1000 hover:bg-[#333333] ${
                isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'
              }`}
              style={{ transitionDelay: `${index * 100}ms` }}
            >
              {/* Rating Stars */}
              <div className="flex gap-1 mb-4 sm:mb-6">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <i
                    key={i}
                    className="ri-star-fill text-[#00D9FF] text-lg sm:text-2xl"
                  ></i>
                ))}
              </div>

              {/* Quote */}
              <div className="relative mb-6 sm:mb-8">
                <i className="ri-double-quotes-l absolute -top-3 -left-1 sm:-top-4 sm:-left-2 text-4xl sm:text-6xl text-[#00D9FF] opacity-30"></i>
                <p className="text-white text-sm sm:text-base md:text-lg font-light leading-relaxed relative z-10 pl-6 sm:pl-8">
                  {testimonial.quote}
                </p>
              </div>

              {/* Client Info */}
              <div className="flex items-center gap-3 sm:gap-4 pt-4 sm:pt-6 border-t border-gray-700">
                <img
                  src={testimonial.avatar}
                  alt={testimonial.name}
                  className="w-12 h-12 sm:w-16 sm:h-16 rounded-full object-cover"
                />
                <div>
                  <h4 className="text-white font-bold text-sm sm:text-lg">
                    {testimonial.name}
                  </h4>
                  <p className="text-gray-400 text-xs sm:text-sm">
                    {testimonial.role}
                  </p>
                  <p className="text-gray-500 text-xs sm:text-sm font-bold mt-0.5 sm:mt-1">
                    {testimonial.company}
                  </p>
                </div>
              </div>
            </div>
          ))}
          {/* Spacer to allow last card to be fully scrollable */}
          <div className="flex-shrink-0 w-4 sm:w-[315px]"></div>
        </div>

        {/* Navigation Controls */}
        <div className="flex gap-3 sm:gap-4 mt-6 sm:mt-8 justify-center sm:justify-start">
          <button
            onClick={() => scroll('left')}
            disabled={!canScrollLeft}
            className={`w-11 h-11 sm:w-14 sm:h-14 rounded-full flex items-center justify-center transition-all duration-300 ${
              canScrollLeft
                ? 'bg-[#00D9FF] text-black hover:scale-110 cursor-pointer'
                : 'bg-gray-800 text-gray-600 opacity-30 cursor-not-allowed'
            }`}
          >
            <i className="ri-arrow-left-line text-lg sm:text-xl"></i>
          </button>
          <button
            onClick={() => scroll('right')}
            disabled={!canScrollRight}
            className={`w-11 h-11 sm:w-14 sm:h-14 rounded-full flex items-center justify-center transition-all duration-300 ${
              canScrollRight
                ? 'bg-[#00D9FF] text-black hover:scale-110 cursor-pointer'
                : 'bg-gray-800 text-gray-600 opacity-30 cursor-not-allowed'
            }`}
          >
            <i className="ri-arrow-right-line text-lg sm:text-xl"></i>
          </button>
        </div>
      </div>

      {/* Hide native scrollbar */}
      <style>{`
        div::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </section>
  );
}
